/**
 * Navigation Timing & SPA Route Transition Tracker
 */

import { telemetryService } from './telemetryService';

class NavigationTracker {
  private activeRouteStart = new Map<string, number>();
  private currentRoute = '/';

  /**
   * Record initial page load timings from W3C Navigation Timing API
   */
  public recordInitialPageLoad(): void {
    if (typeof window === 'undefined' || typeof performance === 'undefined') return;

    window.addEventListener('load', () => {
      setTimeout(() => {
        try {
          const navEntries = performance.getEntriesByType('navigation') as PerformanceNavigationTiming[];
          if (!navEntries || navEntries.length === 0) return;

          const nav = navEntries[0];
          if (!nav) return;
          const startTime = nav.startTime ?? 0;
          telemetryService.recordNavigation({
            route: window.location.pathname,
            durationMs: nav.duration ?? 0,
            dnsTime: (nav.domainLookupEnd ?? 0) - (nav.domainLookupStart ?? 0),
            tcpTime: (nav.connectEnd ?? 0) - (nav.connectStart ?? 0),
            ttfb: (nav.responseStart ?? 0) - (nav.requestStart ?? 0),
            domContentLoadTime: (nav.domContentLoadedEventEnd ?? 0) - startTime,
            fullLoadTime: (nav.loadEventEnd ?? 0) - startTime,
          });
        } catch {
          // Fallback if navigation timing is restricted
        }
      }, 0);
    });
  }

  /**
   * Start tracking a client-side route transition
   */
  public startRouteTransition(toRoute: string): void {
    this.activeRouteStart.set(toRoute, performance.now());
  }

  /**
   * Complete client-side route transition
   */
  public endRouteTransition(toRoute: string): void {
    const startTime = this.activeRouteStart.get(toRoute);
    if (!startTime) return;

    const durationMs = performance.now() - startTime;
    this.activeRouteStart.delete(toRoute);

    telemetryService.recordNavigation({
      route: toRoute,
      previousRoute: this.currentRoute,
      durationMs,
    });

    this.currentRoute = toRoute;
  }
}

export const navigationTracker = new NavigationTracker();
